import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Search, Plus, Globe, ChevronRight, Users, 
  TrendingUp, Bell, Zap, Filter, RefreshCcw 
} from "lucide-react";
import { listCompetitors, createCompetitor } from "@/lib/api";

export default function CompetitorsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("watchlist");
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDomain, setNewDomain] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await listCompetitors();
        if (response.success) {
          setCompetitors(response.data.competitors || []);
        }
      } catch (e) {
        console.error("Load failed", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAdd = async () => {
    try {
      const created = await createCompetitor({ brandName: newName, domain: newDomain });
      setCompetitors(prev => [created, ...prev]);
      setIsAdding(false);
      setNewName("");
      setNewDomain("");
    } catch (e) {
      alert("Failed to add competitor");
    }
  };

  const filteredCompetitors = competitors.filter(c => 
    c.brand_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.domain?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const marketAlerts = competitors.flatMap(c => (c.alerts || []).map(a => ({ ...a, brandName: c.brand_name })))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8 fade-in">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Intelligence Hub</h1>
          <p className="text-sm text-gray-500">Consolidated market monitoring and competitive signals.</p>
        </div>
        <Button 
          onClick={() => setIsAdding(true)} 
          className="bg-primary hover:bg-radar-secondary text-primary-foreground font-medium shadow-sm"
        >
          <Plus className="w-4 h-4 mr-2" /> Track New Brand
        </Button>
      </div>

      {/* Primary Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100/50 p-1 rounded-xl w-fit">
        {[
          { id: "watchlist", label: "Watchlist", icon: Users },
          { id: "pulse", label: "Growth Pulse", icon: TrendingUp },
          { id: "alerts", label: "Market Alerts", icon: Bell }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-lg transition-all ${
              activeTab === tab.id 
                ? "bg-white text-primary shadow-sm" 
                : "text-gray-500 hover:text-gray-700 hover:bg-white/50"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content: Watchlist */}
      {activeTab === "watchlist" && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search your watchlist..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/25 outline-none transition-all"
              />
            </div>
            <Button variant="outline" className="text-gray-500 border-gray-200">
              <Filter className="w-4 h-4 mr-2" /> Filters
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompetitors.map((comp) => (
              <Link 
                key={comp.id} 
                to={`/dashboard/competitors/${comp.id}`}
                className="group bg-white border border-gray-200 p-5 rounded-2xl hover:shadow-xl hover:border-primary/25 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center font-bold text-primary">
                      {comp.brand_name?.[0]}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 group-hover:text-primary transition-colors">{comp.brand_name}</h3>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Globe className="w-3 h-3" /> {comp.domain}
                      </p>
                    </div>
                  </div>
                  <Badge variant={comp.sync_status === 'complete' ? 'success' : 'secondary'} className="text-[10px]">
                    {comp.sync_status === 'complete' ? 'VERIFIED' : 'SYNCING'}
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-4 border-t border-gray-50 pt-4">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Ads</p>
                    <p className="text-sm font-bold text-gray-900">{comp.ad_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Growth</p>
                    <p className="text-sm font-bold text-emerald-600">+{comp.ad_velocity || 0}%</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Stock</p>
                    <p className="text-sm font-bold text-gray-900">{comp.products?.length || 0} SKUs</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Tab Content: Growth Pulse */}
      {activeTab === "pulse" && (
        <div className="grid grid-cols-1 gap-4">
          {competitors.filter(c => (c.ad_velocity || 0) > 0).map(comp => (
            <div key={comp.id} className="bg-white p-6 rounded-2xl border border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Zap className="w-6 h-6 text-yellow-500" />
                <div>
                  <p className="font-bold text-gray-900">{comp.brand_name} is scaling aggressively</p>
                  <p className="text-sm text-gray-500">Ad volume increased by {comp.ad_velocity}% in the last 24 hours.</p>
                </div>
              </div>
              <Link to={`/dashboard/competitors/${comp.id}`}>
                <Button variant="outline" size="sm">View Strategy</Button>
              </Link>
            </div>
          ))}
          {competitors.filter(c => (c.ad_velocity || 0) > 0).length === 0 && (
            <div className="text-center py-20 text-gray-400 italic bg-gray-50 rounded-2xl border border-dashed border-gray-200">
              No scaling anomalies detected in the last 24 hours.
            </div>
          )}
        </div>
      )}

      {/* Tab Content: Market Alerts */}
      {activeTab === "alerts" && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden divide-y divide-gray-100 shadow-sm">
          {marketAlerts.length > 0 ? marketAlerts.map(alert => (
            <div key={alert.id} className="p-5 flex items-start gap-4 hover:bg-gray-50 transition-colors">
              <div className={`p-2 rounded-lg ${alert.alert_type === 'stock_out' ? 'bg-red-50 text-red-600' : 'bg-primary/10 text-primary'}`}>
                <Bell className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold text-gray-900">{alert.brandName}</p>
                  <p className="text-[10px] text-gray-400 uppercase font-bold">{new Date(alert.created_at).toLocaleDateString()}</p>
                </div>
                <p className="text-sm text-gray-600">{alert.message}</p>
              </div>
            </div>
          )) : (
            <div className="p-20 text-center text-gray-400 italic">No market signals captured yet.</div>
          )}
        </div>
      )}

      {/* Add Competitor Modal */}
      {isAdding && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 animate-in slide-in-from-bottom-4 duration-300">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Track New Brand</h2>
            <p className="text-sm text-gray-500 mb-6">Radar will perform a deep scan of their ads, inventory, and tech stack.</p>
            <div className="space-y-4 mb-8">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase">Brand Name</label>
                <input 
                  autoFocus
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-primary/25 transition-all"
                  placeholder="e.g. Warby Parker"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase">Domain</label>
                <input 
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-primary/25 transition-all"
                  placeholder="e.g. warbyparker.com"
                  value={newDomain}
                  onChange={e => setNewDomain(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setIsAdding(false)} className="flex-1 rounded-xl">Cancel</Button>
              <Button onClick={handleAdd} className="flex-1 bg-primary hover:bg-radar-secondary text-primary-foreground rounded-xl shadow-md">Start Deep Scan</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
